"use client";

import { Track } from "@/src/lib/tracking";

/**
 * Chat 广告承接（EDG-543）的**唯一**埋点门面 —— 收口 5 个 `adchat_*` 事件。
 *
 * 为什么要门面：五个事件共用同一组归因参数（`ad_source` / `landing_id`），而
 * 「首屏曝光」「第一条消息」按 session 去重的规则散在调用点必然漏一处。
 *
 * ⚠️ **不带 `landing_card_type`（也不带 `card_type`）** —— 本方案无素材表、无卡型概念，
 * 广告会话一律用 `ad_source` 区分（设计文档 §7.3 架构约束 6）。
 *
 * ⚠️ 事件名必须是**字面量**：`gen-openreplay-events.mjs` 靠静态扫描，
 * 做成 `emit(name)` 的透传形参会扫不到，事件就不会出现在总表里。
 *
 * ⚠️ 参数 key **只能小写**（`AGENTS.md`：camelCase 会被 DOM 静默折掉）。
 */
export const AD_CHAT_EVENT_ENTRY_VIEW = "adchat_entry_view";
export const AD_CHAT_EVENT_PROMPT_TAP = "adchat_prompt_tap";
export const AD_CHAT_EVENT_FIRST_MESSAGE_SENT = "adchat_first_message_sent";
export const AD_CHAT_EVENT_PROMPT_FALLBACK = "adchat_prompt_fallback";
export const AD_CHAT_EVENT_ENTRY_EXIT = "adchat_entry_exit";

/** 四条问题的来源：命中配置素材 / 回落默认问题。 */
export type AdChatPromptSource = "material" | "default";

/** `platform` 恒发 `h5` —— 与 `MoneyDashboardAnalytics` 同口径，区分的是布局不是端。 */
const PLATFORM = "h5";

export interface AdChatAttribution {
  adSource: string | null | undefined;
  landingId: string | null | undefined;
  /**
   * 投放侧 campaign / creative。
   * web 侧目前**无数据源**（Adjust attribution 只在原生侧可读），缺省即不带该字段 ——
   * 宁可缺字段也不造值；后台按 `adjust_adid`（envelope 自带）也能 join 回投放侧。
   */
  adCampaignId?: string | null;
  adCreativeId?: string | null;
}

/** 本会话已上报过的去重标记（**不落盘**，与问题组会话语义一致）。 */
const sessionReported = new Set<string>();

/** 仅供单测重置会话态。 */
export function resetAdChatTrackSessionForTest(): void {
  sessionReported.clear();
}

/** 归因参数；`landing_id` 为空即非广告会话 → 返回 null，调用方整条不发。 */
function attributionParams(
  input: AdChatAttribution,
): Record<string, unknown> | null {
  const landingId = input.landingId?.trim();
  if (!landingId) {
    return null;
  }
  const params: Record<string, unknown> = {
    landing_id: landingId,
    ad_source: input.adSource?.trim() ?? "",
    platform: PLATFORM,
  };
  const campaign = input.adCampaignId?.trim();
  if (campaign) {
    params.ad_campaign_id = campaign;
  }
  const creative = input.adCreativeId?.trim();
  if (creative) {
    params.ad_creative_id = creative;
  }
  return params;
}

/** Ask Ed 首屏渲染完成；**同会话只发一次**（指标按 session 去重）。 */
export function adChatEntryView(
  input: AdChatAttribution & { promptSource: AdChatPromptSource },
): void {
  const base = attributionParams(input);
  if (!base || sessionReported.has(AD_CHAT_EVENT_ENTRY_VIEW)) {
    return;
  }
  sessionReported.add(AD_CHAT_EVENT_ENTRY_VIEW);
  Track.view(AD_CHAT_EVENT_ENTRY_VIEW, {
    ...base,
    prompt_source: input.promptSource,
  });
}

/**
 * 点击四条之一。
 * `position` 是配置序（0 基）—— 四条内容可能改文案，靠它才能定位「点的是第几条」。
 */
export function adChatPromptTap(
  input: AdChatAttribution & {
    promptId: string;
    promptSource: AdChatPromptSource;
    position: number;
  },
): void {
  const base = attributionParams(input);
  if (!base) {
    return;
  }
  Track.event(AD_CHAT_EVENT_PROMPT_TAP, {
    ...base,
    prompt_id: input.promptId,
    prompt_source: input.promptSource,
    position: input.position,
  });
}

/** 第一条消息发出；**同会话只发一次**（「第一条」的定义）。 */
export function adChatFirstMessageSent(
  input: AdChatAttribution & { inputType: "prompt" | "typed" },
): void {
  const base = attributionParams(input);
  if (!base || sessionReported.has(AD_CHAT_EVENT_FIRST_MESSAGE_SENT)) {
    return;
  }
  sessionReported.add(AD_CHAT_EVENT_FIRST_MESSAGE_SENT);
  Track.event(AD_CHAT_EVENT_FIRST_MESSAGE_SENT, {
    ...base,
    input_type: input.inputType,
  });
}

/** 判定整组取不到（`not_enough_prompts` / `empty_prompt` / `timeout`）。 */
export function adChatPromptFallback(
  input: AdChatAttribution & { reason: string },
): void {
  const base = attributionParams(input);
  if (!base) {
    return;
  }
  Track.event(AD_CHAT_EVENT_PROMPT_FALLBACK, {
    ...base,
    reason: input.reason,
  });
}

/** 离开首屏（未发消息时 `message_sent=false`）。 */
export function adChatEntryExit(
  input: AdChatAttribution & {
    messageSent: boolean;
    exitType: "close" | "background";
  },
): void {
  const base = attributionParams(input);
  if (!base) {
    return;
  }
  Track.event(AD_CHAT_EVENT_ENTRY_EXIT, {
    ...base,
    message_sent: input.messageSent,
    exit_type: input.exitType,
  });
}
